import type { LessonBlockData, ReadyLessonStep } from '../lib/api';

export type ReadyStageSelectionRow = Pick<ReadyLessonStep, 'step_order' | 'stage_key' | 'block_id'>;

const preferredBlockTypes: Record<string, readonly string[]> = {
  warmup: ['dialogue', 'image', 'interactive', 'reading'],
  learn: ['vocabulary', 'grammar'],
  practice: ['exercise', 'interactive', 'revision'],
  listen: ['listening', 'video', 'media_sequence', 'reading'],
  speak: ['speaking', 'dialogue', 'interactive'],
  transfer: ['writing', 'exercise'],
  review: ['revision', 'exercise', 'interactive'],
};

/**
 * The ready-lesson plan's block_id is the source of truth for teacher delivery.
 * Only use a stage-type fallback when that explicit link is absent or stale;
 * never silently attach an arbitrary unrelated block to a teaching stage.
 */
export function selectReadyStageBlocks(
  rows: ReadyStageSelectionRow[],
  sourceBlocks: LessonBlockData[],
): Map<number, LessonBlockData | null> {
  const used = new Set<string>();
  const result = new Map<number, LessonBlockData | null>();

  for (const row of rows) {
    const linked = row.block_id
      ? sourceBlocks.find((block) => block.id === row.block_id && !used.has(block.id))
      : undefined;
    const preferred = preferredBlockTypes[row.stage_key] || [];
    const fallback = sourceBlocks.find(
      (block) => !used.has(block.id) && preferred.includes(block.block_type),
    );
    const block = linked || fallback || null;

    if (block) used.add(block.id);
    result.set(row.step_order, block);
  }

  return result;
}


/** Return a visible label only for explicitly named source-section plans. */
export function readyPlanLabel(profile: string): string | null {
  const prefix = 'source:';
  if (!profile.startsWith(prefix)) return null;
  const label = profile.slice(prefix.length).trim();
  return label || null;
}
